import type {
  GetListRequestV2,
  GetListResponseOfInspectionTypeSimpleResponse,
  GetListResponseOfSelectOption,
  InspectionTypeEquipmentTypeResponse,
  InspectionTypeRequest,
  InspectionTypeResponse,
} from 'src/api/ProactApiClient';
import {inspectionTypeApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

// Tag writes ride on EquipmentType update, so EquipmentTypes also invalidates on InspectionType
// mutations (and vice versa) to keep the tag multi-select and covered-equipment view fresh.
const INSPECTION_TYPE_TAGS = ['InspectionTypes', 'EquipmentTypes'] as const;

export const inspectionTypeApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getInspectionTypeSelectOptions: builder.query<
      GetListResponseOfSelectOption,
      GetListRequestV2
    >({
      queryFn: clientQuery(params =>
        inspectionTypeApiClient.listSelectOptions(params)
      ),
      providesTags: ['InspectionTypes'],
    }),
    getInspectionTypeListV2: builder.query<
      GetListResponseOfInspectionTypeSimpleResponse,
      GetListRequestV2
    >({
      queryFn: clientQuery(params => inspectionTypeApiClient.listV2(params)),
      providesTags: ['InspectionTypes'],
    }),
    getInspectionType: builder.query<InspectionTypeResponse, number>({
      queryFn: clientQuery(id => inspectionTypeApiClient.get(id)),
      providesTags: ['InspectionTypes'],
    }),
    getInspectionTypeEquipmentTypes: builder.query<
      InspectionTypeEquipmentTypeResponse[],
      number
    >({
      queryFn: clientQuery(id => inspectionTypeApiClient.getEquipmentTypes(id)),
      providesTags: ['InspectionTypes', 'EquipmentTypes'],
    }),
    createInspectionType: builder.mutation<
      InspectionTypeResponse,
      InspectionTypeRequest
    >({
      queryFn: clientQuery(request => inspectionTypeApiClient.create(request)),
      invalidatesTags: [...INSPECTION_TYPE_TAGS],
    }),
    updateInspectionType: builder.mutation<
      InspectionTypeResponse,
      InspectionTypeRequest
    >({
      queryFn: clientQuery(request => inspectionTypeApiClient.update(request)),
      invalidatesTags: [...INSPECTION_TYPE_TAGS],
    }),
    deleteInspectionType: builder.mutation<boolean, number>({
      queryFn: clientQuery(id => inspectionTypeApiClient.delete(id)),
      invalidatesTags: [...INSPECTION_TYPE_TAGS],
    }),
    updateInspectionTypeActiveStatus: builder.mutation<
      InspectionTypeResponse,
      {id: number; active: boolean}
    >({
      queryFn: clientQuery(({id, active}) =>
        inspectionTypeApiClient.updateActiveStatus(id, active)
      ),
      invalidatesTags: [...INSPECTION_TYPE_TAGS],
    }),
  }),
});

export const {
  useGetInspectionTypeSelectOptionsQuery,
  useGetInspectionTypeListV2Query,
  useGetInspectionTypeQuery,
  useGetInspectionTypeEquipmentTypesQuery,
  useCreateInspectionTypeMutation,
  useUpdateInspectionTypeMutation,
  useDeleteInspectionTypeMutation,
  useUpdateInspectionTypeActiveStatusMutation,
} = inspectionTypeApi;
